/**
 * @module ThumbCarousel
 * @description
 */
import React, { FC,  useState, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import Image from 'next/image';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore from 'swiper'
import { A11y, Autoplay, Controller, EffectFade } from 'swiper/modules';
import { fastly, fastlyBlur } from 'ui/js/utils/helpers';
import { ImageProps } from '~/types/common';
import Component from 'ui/components/Component';
import TrackElementViewed from '~/components/helpers/TrackElementViewed';

import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/autoplay';
import styles from './ThumbCarousel.module.css';

interface Slide {
  heading: string;
  copy: string | JSX.Element;
  image: ImageProps;
}

interface Props {
  slides: Slide[];
  position: string; // Position of the carousel for tracking purposes
}

const ThumbCarousel: FC<Props> = ( { ...props } ) => {
  const {
    slides,
    position,
  } = props;

  const autoplayTime = 10000;
  const [ paused, setPaused ] = useState( true );
  const [ imageSwiper, setImageSwiper ] = useState<SwiperCore>();
  const [ contentSwiper, setContentSwiper ] = useState<SwiperCore>();
  const sharedCarouselProps = {
    modules: [ A11y, Autoplay, Controller, EffectFade ],
    direction: 'vertical',
    autoplay: {
      delay: autoplayTime,
      disableOnInteraction: false,
      pauseOnMouseEnter: true
    }
  };

  const options = {
    threshold: 0.3,
    triggerOnce: false
  };
  const { ref, inView } = useInView( options );

  useEffect( () => {
    if( ! imageSwiper || ! imageSwiper.autoplay ) {
      return;
    }

    if ( inView ) {
      imageSwiper.autoplay.start();
    } else {
      imageSwiper.autoplay.stop();
    }

    setPaused( !inView );
  }, [ inView, imageSwiper ] );

  useEffect( () => {
    if( ! imageSwiper || ! contentSwiper ) {
      return;
    }

    if( imageSwiper.destroyed || contentSwiper.destroyed ) {
      return;
    }

    contentSwiper.controller.control = imageSwiper;
    imageSwiper.controller.control = contentSwiper;
  }, [ imageSwiper, contentSwiper ] );

  return (
    <Component
      className={ styles.component }
    >
      <div ref={ ref } className={ styles.container }>
        <Swiper
          className={ styles.imageSwiper }
          onSwiper={ setImageSwiper }
          slidesPerView={ 1 }
          grabCursor={ true }
          effect="fade"
          fadeEffect={ { crossFade: true } }
          { ...sharedCarouselProps }
        >
          { slides.map( ( slide: Slide, index: number ) => (
            <SwiperSlide key={ index } className={ styles.slide }>
              <TrackElementViewed trackOpacity flex position={`${position} - CAROUSEL - SLIDE ${ index + 1 }`}>
                <Image
                  src={ slide.image.src }
                  alt={ slide.image.alt }
                  loader={ fastly }
                  loading="lazy"
                  width={ 1137 }
                  height={ 554 }
                  placeholder="blur"
                  blurDataURL={ fastlyBlur( slide.image.src ) }
                  className={ slide.image.className || `${ styles.thumbCarouselImage } image-${ index + 1 }` }
                />
              </TrackElementViewed>
            </SwiperSlide>
          ) ) }
        </Swiper>

        <Swiper
          className={ styles.contentSwiper }
          onSwiper={ setContentSwiper }
          slidesPerView={ slides.length }
          spaceBetween={ 32 }
          virtualTranslate={ true }
          centeredSlides={ true }
          slideToClickedSlide={ true }
          grabCursor={ true }
          { ...sharedCarouselProps }
        >
          { slides.map( ( slide: Slide, index: number ) => (
            <SwiperSlide key={ index } className={ styles.slide }>
              <div className={ styles.slide__info }>
                <span
                  className={ styles.progress }
                  style={ {
                    animationDuration: `${ ( autoplayTime / 1000 ) }s`,
                    animationPlayState: `${ paused ? 'paused' : 'running' }`
                  } }
                ></span>
                <h2 className={ styles.slide__heading }>{ slide.heading }</h2>
                <p className={ styles.slide__copy }>{ slide.copy }</p>
              </div>
            </SwiperSlide>
          ) ) }
        </Swiper>
      </div>
    </Component>
  );
};

ThumbCarousel.displayName = 'ThumbCarousel';

export default ThumbCarousel;
